import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output, ViewChild } from '@angular/core';
import {
  ActiveLookup,
  BUSINESS_PERSONAL_TYPE,
  LayoutGridType,
  LinkedInType,
  LookupKeys,
  Lookups,
  Messages,
  Social,
  SocialKeys,
  YoutubeType,
} from '@ag-common-lib/public-api';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { SocialsService } from '../socials.service';
import { validateDxGroups, validateUrlWithOrWithoutProtocol } from 'ag-common-svc/lib/utils/validation';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ModalWindowComponent } from '../../modal-window/modal-window.component';
import { SocialFormComponent } from '../components/social-form/social-form.component';
import * as uuid from 'uuid';
import { SocialNameFromUrlPipe } from '../social-name-from-url.pipe';

@Component({
  selector: 'ag-shr-socials-grid',
  templateUrl: './socials-grid.component.html',
  styleUrls: ['./socials-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SocialNameFromUrlPipe],
})
export class SocialsGridComponent {
  @HostBinding('class') className = 'socials-grid';
  @ViewChild('socialsEditorModalRef', { static: true }) socialsEditorModalComponent: ModalWindowComponent;
  @ViewChild('socialFormRef', { static: false }) socialFormComponent: SocialFormComponent;

  @Input() gridTitle: string = 'Socials';
  @Input() hasPermissionsToEdit: boolean = true;
  @Input() hasPermissionsToDelete: boolean = true;
  @Input() hasPermissionsToAdd: boolean = true;
  @Input() selectionMode: 'none' | 'multiple' = 'none';
  @Input() socials: Social[] = [];
  @Input() agentDbId: string | null = null;
  @Input()
  set socialTypesLookup(items: ActiveLookup[]) {
    this._socialTypesLookup = items;
    this._defaultSocialTypeLookup = items?.find(item => item?.isDefault);
  }
  get socialTypesLookup(): ActiveLookup[] {
    return this._socialTypesLookup;
  }
  @Input() socialMediaLookup: ActiveLookup[] = [];
  @Input() saveSocials: (socials: Social[]) => Promise<Social[]>;
  @Input() selectedRowKeys: any[];
  @Input() type: LayoutGridType = LayoutGridType.short;
  @Output() selectedRowKeysChange = new EventEmitter<any[]>();

  inProgress$ = new BehaviorSubject<boolean>(false);

  protected inEditMode$ = new BehaviorSubject<boolean>(false);
  protected readonly Messages = Messages;
  protected readonly SocialKeys = SocialKeys;
  protected readonly LookupKeys = LookupKeys;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly LayoutGridType = LayoutGridType;
  protected readonly businessPersonalType = BUSINESS_PERSONAL_TYPE;
  public Lookups = Lookups;
  protected readonly socialValidationGroup = 'socialValidationGroup';
  protected socialToEdit: Social;
  readonly emptyMessage = 'No Socials Currently Exist';
  private _defaultSocialTypeLookup: ActiveLookup;
  private _socialTypesLookup: ActiveLookup[] = [];
  socialsModalTitle = 'Add / Edit Socials';

  constructor(
    private toastrService: ToastrService,
    private socialsService: SocialsService,
    private socialNameFromUrlPipe: SocialNameFromUrlPipe,
  ) {}

  onInitNewRow = (e): void => {
    e.data.social_type = this._defaultSocialTypeLookup?.dbId;
  };

  onRowRemoving = e => {
    const socials: Social[] = this.socials.filter(address => {
      return address !== e.key;
    });
    this.updateSocials(socials);
    e.cancel = false;
  };

  validateUrlWithOrWithoutProtocol = e => validateUrlWithOrWithoutProtocol(e?.value);

  onSocialTypeChanged = ({ selectedItem }): void => {
    this.socialsService.setSelectedSocialTypeChange(selectedItem);
  };

  onSocialMediaChanged = ({ selectedItem }): void => {
    this.socialsService.setSelectedSocialMedia(selectedItem);
  };

  gotoSocial = socialUrl => {
    window.open(socialUrl, '_blank');
  };

  onValidateAccountClick = e => {
    const socialUrl = this.socialFormComponent?.formData[SocialKeys.url];
    if (socialUrl) {
      this.gotoSocial(socialUrl);
    }
  };

  protected handleSocialMediaSave = async e => {
    this.inProgress$.next(true);

    const isValid = await validateDxGroups([this.socialValidationGroup]);
    if (!isValid) {
      this.inProgress$.next(false);
      return;
    }

    const isUniq = this.checkIsSocialUniq(this.socialToEdit);
    if (!isUniq) {
      this.toastrService.error('Same Social already exists in this profile');
      this.inProgress$.next(false);
      return;
    }

    const socials = this._normalizeSocials();
    try {
      await this.updateSocials(socials);

      this.socialsEditorModalComponent.forceCloseModal();
    } catch (error) {
      this.inProgress$.next(false);
    }
  };

  protected handleSocialMediaClosePopup = () => {
    return this.socialFormComponent.onCancelEdit();
  };

  protected handleEditSocials = e => {
    const data = e?.row?.data;
    this.socialsModalTitle = !data ? 'Add Social' : 'Edit Social';
    const url = data?.[SocialKeys.url] ?? '';
    const socialMedia = data?.[SocialKeys.socialMedia] ?? null;
    const accountId = data?.[SocialKeys.accountId] ?? this._getUserName(data);

    this.socialToEdit = Object.assign({}, new Social(), {
      [SocialKeys.id]: data?.[SocialKeys.id] ?? uuid.v4(),
      [SocialKeys.url]: url,
      [SocialKeys.socialType]: data?.[SocialKeys.socialType] ?? null,
      [SocialKeys.socialMedia]: socialMedia,
      [SocialKeys.accountId]: accountId,
      [SocialKeys.accountType]: data?.[SocialKeys.accountType] ?? this._getAccountType(url, socialMedia),
    });
    this.socialsEditorModalComponent.showModal();
  };

  protected enableEditMode = () => {
    this.inEditMode$.next(true);
  };

  protected disableEditMode = () => {
    this.inEditMode$.next(false);
  };

  private checkIsSocialUniq = data => {
    return this.socials.every(social => {
      if (social?.id === data?.id) {
        return true;
      }

      return data?.url !== social?.url;
    });
  };

  private _normalizeSocials = (): Social[] => {
    if (!this.socialToEdit) {
      return this.socials;
    }
    let isUpdated = false;
    const normalizedSocials = this.socials.map(social => {
      if (this.socialToEdit?.[SocialKeys.id] === social?.[SocialKeys.id]) {
        isUpdated = true;
        return this.socialToEdit;
      }
      const normalizedSocial = Object.assign({}, social);

      return normalizedSocial;
    });

    if (!isUpdated) {
      normalizedSocials.push(Object.assign({}, this.socialToEdit));
    }
    return normalizedSocials;
  };

  private updateSocials = (socials: Social[]) => {
    this.inProgress$.next(true);
    Promise.all([this.saveSocials(socials), this.socialsService.onUpdateSocials()])
      .then(() => {
        this.socials = socials;
        this.toastrService.success('Socials updated!');
      })
      .catch(() => {
        this.toastrService.error('Socials are not updated!');
      })
      .finally(() => {
        this.inProgress$.next(false);
      });
  };

  private _getUserName = (social: Social): string => {
    const url = social?.[SocialKeys.url] ?? '';
    if (!url) {
      return null;
    }

    const userName = this.socialNameFromUrlPipe.getLastSegmentOfUrl(url);
    return userName ?? null;
  };

  private _getAccountType = (url: string, socialMedia: string): string => {
    if (!url || !socialMedia || !['LI', 'YT'].includes(socialMedia)) {
      return null;
    }

    switch (socialMedia) {
      case 'LI':
        return url.includes(LinkedInType.Public) ? LinkedInType.Public : LinkedInType.Company;

      case 'YT':
        return Object.values(YoutubeType).find(key => url.includes(`/${key}`)) ?? null;

      default:
        return null;
    }
  };
}
